import localforage from 'localforage';
import LZString from 'lz-string';

const COMPRESS_PREFIX = 'lzc_';

/**
 * Gets the current user prefix for scoping keys.
 */
function getKeysPrefix(userId?: string | null): string {
  return userId ? `user_${userId}_` : 'anon_';
}

/**
 * Saves compressed data scoped to the current user (or anonymous).
 */
export async function setStorageData<T>(key: string, data: T, userId?: string | null): Promise<void> {
  try {
    const rawData = JSON.stringify(data);
    const compressedData = LZString.compressToUTF16(rawData);
    const fullKey = `${getKeysPrefix(userId)}${key}`;
    await localforage.setItem(fullKey, `${COMPRESS_PREFIX}${compressedData}`);
  } catch (error) {}
}

/**
 * Gets data scoped to the current user (or anonymous), automatically decompressing if needed.
 */
export async function getStorageData<T>(key: string, userId?: string | null): Promise<T | null> {
  try {
    const fullKey = `${getKeysPrefix(userId)}${key}`;
    const rawData = await localforage.getItem<string>(fullKey);
    
    if (!rawData) {
      // Fallback: If not found under prefixed key, try to migrate from global un-prefixed key
      // This helps users keep their existing data when updating the app
      const fallbackData = await localforage.getItem<any>(key);
      if (fallbackData) {
        // We migrate it to the current user's scope
        await setStorageData(key, fallbackData, userId);
        // We'll leave the old data there just in case, or we can remove it. Let's keep it safe for now.
        return fallbackData as T;
      }
      return null;
    }

    if (typeof rawData === 'string' && rawData.startsWith(COMPRESS_PREFIX)) {
      const compressedStr = rawData.substring(COMPRESS_PREFIX.length);
      const decompressed = LZString.decompressFromUTF16(compressedStr);
      if (!decompressed) return null;
      return JSON.parse(decompressed) as T;
    }

    // Older uncompressed structure but within user scope
    if (typeof rawData === 'string') {
      try {
        return JSON.parse(rawData) as T;
      } catch {
        return rawData as any as T;
      }
    }

    return rawData as T;
  } catch (error) {
    return null;
  }
}

/**
 * Removes data scoped to the current user.
 */
export async function removeStorageData(key: string, userId?: string | null): Promise<void> {
  try {
    const fullKey = `${getKeysPrefix(userId)}${key}`;
    await localforage.removeItem(fullKey);
  } catch (error) {}
}

/**
 * Clears ONLY the specific user's (or anon's) data from localforage, leaving other users intact.
 */
export async function clearUserStorageData(userId?: string | null): Promise<void> {
  try {
    const prefix = getKeysPrefix(userId);
    const keys = await localforage.keys();
    for (const key of keys) {
      if (key.startsWith(prefix)) {
        await localforage.removeItem(key);
      }
    }
  } catch (error) {}
}
